import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Chip, IconButton } from "@mui/material";
import { useTablePagination, useAppMeta, useAuth } from "hooks";

import AddIcon from "@mui/icons-material/Add";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BaseSelect from "components/Shared/Base/BaseSelect";
import HighlightedText from "components/Shared/HighlightedText";

import {
  useLazyGetCocCollectionListQuery,
  useLazyListCocCollectionBillingPrintQuery,
} from "store/services/cocCollectionApi";
import { useLazyGetDDLUserListQuery } from "store/services/userApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { BaseTabs } from "components/Shared/BaseTabs";
import ExportCocCollectionExcel from "./FinanceCollection/ExportCocCollectionExcel";
import { PrintOutlined } from "@mui/icons-material";
import { handlePrint } from "./cocCollectionComponents/CocCollectionBillingPrint";

const CocCollectionListScreen = () => {

  const [searchParams] = useSearchParams();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const { isVendor, viewEntity } = useAuth();

  const activeTab = searchParams.get("tab") || "Active";

  const keyword = searchParams.get("search") || "";

  // State for filters
  const [branch, setBranch] = React.useState("");

  const [user, setUser] = React.useState("");

  const [isCollected, setIsCollected] = React.useState("");

  const breadcrumbs = [{ title: "Counter Collection" }];

  const [fetchCocCollectionPrint] = useLazyListCocCollectionBillingPrintQuery();

  const CocCollectionListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.cocCollectionId,
      cell: (row) => {
        const displayId = row?.cocCollectionId;
        const path = isVendor
          ? `/app/vendor/counter-collection/${row?._id}/detail`
          : `/app/crm/coc-collection/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
      minWidth: "200px",
    },
    {
      name: "Branch",
      selector: (row) => row?.branchName,
      cell: (row) => (
        <div title={row?.branchName} className="line-clamp-2">
          {row?.branchName ? (
            <HighlightedText text={row?.branchName} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "200px",
    },
    {
      name: "Requested By",
      selector: (row) => row?.addedUser,
      cell: (row) => row?.addedUser || "N/A",
      minWidth: "150px",
    },
    {
      name: "Amount",
      selector: (row) => row?.collectionAmount,
      cell: (row) => (
        <div title={row?.collectionAmount} className="line-clamp-2">
          {row?.collectionAmount != null
            ? `${row?.collectionAmount?.toFixed(2)}`
            : "N/A"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Method",
      selector: (row) => row?.collectionMethod,
      cell: (row) => (
        <div title={row?.collectionMethod?.name} className="line-clamp-2">
          {row?.collectionMethod?.name || "N/A"}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Remittance Remarks",
      selector: (row) => row?.remittanceRemarks,
      cell: (row) => row?.remittanceRemarks || "N/A",
    },

    {
      name: "Requested At",
      selector: (row) => <AppTableDateTime date={row?.collectionDate} />,
    },
    {
      name: "Financed At",
      selector: (row) => <AppTableDateTime date={row?.financedAt} />,
    },
    {
      name: "Clearance Status",
      selector: (row) => {
        const status = row?.isCollected;
        if (status === true) return "Approved";
        if (status === false) return "Rejected";
        return "Pending";
      },
      cell: (row) => {
        const rawStatus = row?.isCollected;

        const getStatus = (status) => {
          if (status === true || status === "Approved") return "Approved";
          if (status === false || status === "Rejected") return "Rejected";
          return "Pending";
        };

        const getColor = (status) => {
          switch (status) {
            case "Approved":
              return "success";
            case "Rejected":
              return "error";
            case "Pending":
            default:
              return "warning";
          }
        };

        const status = getStatus(rawStatus);

        return (
          <Chip
            label={status}
            color={getColor(status)}
            title={status}
            size="small"
            sx={{
              minWidth: "70px",
              height: 20,
              fontSize: "0.7rem",
              borderRadius: "25px",
            }}
          />
        );
      },
      minWidth: "200px",
    },

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link
            to={
              isVendor
                ? `/app/vendor/counter-collection/${row._id}/detail`
                : `/app/crm/coc-collection/${row._id}/detail`
            }
          >
            <IconButton size="small" aria-label="View COC Collection">
              <InfoOutlined color="primary" />
            </IconButton>
          </Link>
          <IconButton
            size="small"
            aria-label="Print COC Collection"
            onClick={() => handlePrint(row._id, fetchCocCollectionPrint)}
          >
            <PrintOutlined color="primary" />
          </IconButton>
        </>
      ),
    },
  ];

  const [
    fetchCocCollection,
    {
      data: CocCollectionList,
      isLoading: loadingCocCollection,
      isFetching: fetchingCocCollection,
      error,
    },
  ] = useLazyGetCocCollectionListQuery();

  // Fetch branches and users
  const [fetchBranches, { data: branchesData, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();
  const [fetchUsers, { data: usersData, isLoading: loadingUsers }] =
    useLazyGetDDLUserListQuery();

  React.useEffect(() => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      fetchBranches({
        account: viewEntity?.entity?.account?._id,
        entity: viewEntity?.entity?._id,
      });
      fetchUsers({
        account: viewEntity?.entity?.account?._id,
        entity: viewEntity?.entity?._id,
      });
    }
  }, [
    viewEntity?.entity?.account?._id,
    viewEntity?.entity?._id,
    fetchBranches,
    fetchUsers,
  ]);

  React.useEffect(() => {
    fetchCocCollection({
      active: activeTab === "Active" ? "true" : "false",
      keywords: keyword,
      page: currentPage,
      limit: perPage,
      ...(isCollected && { isCollected }),
      ...(branch && { branch }),
      ...(user && { addedUser: user }),
    });
  }, [
    activeTab,
    fetchCocCollection,
    keyword,
    currentPage,
    perPage,
    isCollected,
    branch,
    user,
  ]);

  useAppMeta(`${activeTab} COC Collection`);

  // Prepare options for branch and user dropdowns
  const branchOptions = React.useMemo(() => {
    const validBranches = Array.isArray(branchesData)
      ? branchesData.map((item) => ({
          label: item?.name,
          value: item?._id,
        }))
      : [];
    return [{ label: "All Branches", value: "" }, ...validBranches];
  }, [branchesData]);

  const userOptions = React.useMemo(() => {
    const validUsers = Array.isArray(usersData)
      ? usersData.map((item) => ({
          label: item?.name,
          value: item?._id,
        }))
      : [];
    return [{ label: "All Users", value: "" }, ...validUsers];
  }, [usersData]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={CocCollectionListTabs}
          activeTab={activeTab}
          actions={<ExportCocCollectionExcel />}
        />
        <TableFilter searchLabel="COC Collection ID" initialFilters={3}>
          <BaseAutocomplete
            fullWidth
            label="Branch"
            value={branchOptions.find((opt) => opt.value === branch) || null}
            options={branchOptions}
            onChange={(_, selected) => {
              setBranch(selected?.value ?? "");
            }}
            getOptionLabel={(opt) => opt?.label || "Unknown Branch"}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            disabled={loadingBranches}
          />

          <BaseAutocomplete
            fullWidth
            label="Requested By"
            value={userOptions.find((opt) => opt.value === user) || null}
            options={userOptions}
            onChange={(_, selected) => {
              setUser(selected?.value ?? "");
            }}
            getOptionLabel={(opt) => opt?.label || "Unknown User"}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            disabled={loadingUsers}
          />

          <BaseSelect
            fullWidth
            label="Clearance Status"
            value={isCollected}
            onChange={(e) => setIsCollected(e.target.value)}
            options={[
              { label: "All Statuses", value: "" },
              { label: "Pending", value: "Pending" },
              { label: "Approved", value: "Approved" },
              { label: "Rejected", value: "Rejected" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>
        <AppTable
          columns={columns}
          data={CocCollectionList?.data}
          paginationServer
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={
            loadingCocCollection ||
            fetchingCocCollection ||
            loadingBranches ||
            loadingUsers
          }
          paginationTotalRows={CocCollectionList?.totalItems ?? 0}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default CocCollectionListScreen;

const PageActions = () => {
  const { isVendor } = useAuth();

  return (
    <>
      {!isVendor && (
        <div
          style={{
            display: "flex",
            alignContent: "center",
            justifyContent: "space-between",
          }}
        >
          <Link to="/app/crm/coc-collection/create">
            <Button startIcon={<AddIcon />} variant="contained">
              New Counter Collection
            </Button>
          </Link>
        </div>
      )}
    </>
  );
};
